import React, { useCallback, useEffect, useState } from 'react';
import {
  MPFButton,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';
import { COOKIE_CONSENT } from './constants';

export interface CookieConsentValue {
  statistics: boolean;
  /** Cookies de conteúdos embutidos de terceiros (YouTube, Vimeo). */
  thirdParty: boolean;
  ts: number;
  policyVersion: string;
}

/**
 * Decisão salva pelo usuário, ou `null` se não houve, se venceu (12 meses), se
 * foi dada para outra versão da política ou se não há storage.
 */
export const getCookieConsent = (): CookieConsentValue | null => {
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT.storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const valid =
      typeof parsed?.statistics === 'boolean' &&
      parsed.policyVersion === COOKIE_CONSENT.policyVersion &&
      Date.now() - parsed.ts < COOKIE_CONSENT.maxAgeMs;
    // Escolhas anteriores à categoria de terceiros não a concederam.
    return valid ? { ...parsed, thirdParty: parsed.thirdParty === true } : null;
  } catch {
    return null;
  }
};

export type CookieChoice = Pick<
  CookieConsentValue,
  'statistics' | 'thirdParty'
>;

export const saveCookieConsent = ({ statistics, thirdParty }: CookieChoice) => {
  const value: CookieConsentValue = {
    statistics,
    thirdParty,
    ts: Date.now(),
    policyVersion: COOKIE_CONSENT.policyVersion,
  };
  try {
    window.localStorage.setItem(
      COOKIE_CONSENT.storageKey,
      JSON.stringify(value),
    );
  } catch {
    // Sem storage a escolha vale só nesta sessão da página.
  }
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT.changedEvent, { detail: value }),
  );
};

/** Reabre o painel (ex.: link "Gestão de cookies" do rodapé). */
export const openCookieConsent = () =>
  window.dispatchEvent(new Event(COOKIE_CONSENT.openEvent));

/**
 * Aviso de cookies (LGPD), fixo no rodapé.
 *
 * Volto renderiza no servidor: inicia oculto e só decide no cliente
 * (useEffect), evitando mismatch de hidratação. Sem decisão salva, entra com
 * animação após `showDelayMs`, para não competir com o carregamento da página.
 * Pode ser reaberto (sem atraso) disparando
 * `window.dispatchEvent(new Event('mpf:open-cookie-consent'))`.
 */
export const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [choice, setChoice] = useState<CookieChoice>({
    statistics: false,
    thirdParty: false,
  });

  useEffect(() => {
    // Rechecar ao disparar o timer: a escolha pode ter sido feita em outro
    // ponto (ex.: aviso de um embed) durante o atraso.
    const timer = window.setTimeout(
      () => setVisible(getCookieConsent() === null),
      COOKIE_CONSENT.showDelayMs,
    );
    const open = () => {
      const current = getCookieConsent();
      setChoice({
        statistics: current?.statistics ?? false,
        thirdParty: current?.thirdParty ?? false,
      });
      setCustomizing(false);
      setVisible(true);
    };
    const close = () => setVisible(false);
    window.addEventListener(COOKIE_CONSENT.openEvent, open);
    window.addEventListener(COOKIE_CONSENT.changedEvent, close);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(COOKIE_CONSENT.changedEvent, close);
      window.removeEventListener(COOKIE_CONSENT.openEvent, open);
    };
  }, []);

  const decide = useCallback((value: CookieChoice) => {
    saveCookieConsent(value);
    setVisible(false);
  }, []);

  const categories = [
    {
      key: 'statistics',
      label: COOKIE_CONSENT.statisticsLabel,
      hint: COOKIE_CONSENT.statisticsHint,
    },
    {
      key: 'thirdParty',
      label: COOKIE_CONSENT.thirdPartyLabel,
      hint: COOKIE_CONSENT.thirdPartyHint,
    },
  ] as const;

  if (!visible) return null;

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby="mpf-cookie-consent-title"
      aria-describedby="mpf-cookie-consent-text"
      className="mpf-cookie-consent"
    >
      <div className="mpf-cookie-consent__container">
        <div className="mpf-cookie-consent__content">
          <h2
            id="mpf-cookie-consent-title"
            className="mpf-cookie-consent__title"
          >
            {COOKIE_CONSENT.title}
          </h2>
          <p id="mpf-cookie-consent-text" className="mpf-cookie-consent__text">
            {COOKIE_CONSENT.textBefore}
            <MPFLink
              href={COOKIE_CONSENT.privacyUrl}
              className="mpf-cookie-consent__link"
            >
              {COOKIE_CONSENT.linkLabel}
            </MPFLink>
            {COOKIE_CONSENT.textAfter}
          </p>
          {customizing && (
            <fieldset className="mpf-cookie-consent__options">
              <legend className="sr-only">{COOKIE_CONSENT.customize}</legend>
              {categories.map(({ key, label, hint }) => (
                <div key={key} className="mpf-cookie-consent__option">
                  <input
                    id={`mpf-cookie-${key}`}
                    type="checkbox"
                    checked={choice[key]}
                    aria-describedby={`mpf-cookie-${key}-hint`}
                    onChange={(e) =>
                      setChoice({ ...choice, [key]: e.target.checked })
                    }
                  />
                  <div>
                    <label
                      htmlFor={`mpf-cookie-${key}`}
                      className="mpf-cookie-consent__option-label"
                    >
                      {label}
                    </label>
                    <p
                      id={`mpf-cookie-${key}-hint`}
                      className="mpf-cookie-consent__option-hint"
                    >
                      {hint}
                    </p>
                  </div>
                </div>
              ))}
            </fieldset>
          )}
        </div>
        <div className="mpf-cookie-consent__actions">
          {customizing ? (
            <MPFButton
              variant="primary"
              size="m"
              onClick={() => decide(choice)}
            >
              {COOKIE_CONSENT.save}
            </MPFButton>
          ) : (
            <MPFButton
              variant="ghost"
              size="m"
              onClick={() => setCustomizing(true)}
            >
              {COOKIE_CONSENT.customize}
            </MPFButton>
          )}
          <MPFButton
            variant="primary"
            size="m"
            onClick={() => decide({ statistics: false, thirdParty: false })}
          >
            {COOKIE_CONSENT.reject}
          </MPFButton>
          <MPFButton
            variant="primary"
            size="m"
            onClick={() => decide({ statistics: true, thirdParty: true })}
          >
            {COOKIE_CONSENT.accept}
          </MPFButton>
        </div>
      </div>
    </section>
  );
};

export default CookieConsent;
